import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import {
  EmptyState,
  PageHeader,
} from "../../components/dashboard/DashboardUI";
import { fetchOperatorProcedures } from "../../redux/actions/ProcedureActions";
import { getLatestFollowUps } from "../../redux/actions/followUpActions";
import { REQUEST_TYPES, requestTitle } from "../../utils/requestDisplay";
import {
  FOLLOW_UP_CHANNELS,
  clientName,
  clientOf,
} from "../../utils/salesContact";

const PAGE_SIZE = 25;

// Unfinished (unpaid) requests of the agent's country, to remind clients
function SalesProcedures() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.authUser);
  const { procedures } = useSelector((state) => state.procedures);

  const [loading, setLoading] = useState(true);
  const [followUps, setFollowUps] = useState([]);
  const [search, setSearch] = useState("");
  const [type, setType] = useState("");
  const [reminded, setReminded] = useState("");
  const [visible, setVisible] = useState(PAGE_SIZE);

  useEffect(() => {
    if (!user?.token) return;

    setLoading(true);
    Promise.all([
      dispatch(fetchOperatorProcedures(user.token, user.country)),
      dispatch(getLatestFollowUps(user.token, setFollowUps)),
    ]).finally(() => setLoading(false));
  }, [user?.token, user?.country, dispatch]);

  // Most recent reminder of each request
  const lastFollowUp = useMemo(() => {
    const byRequest = {};
    followUps.forEach((item) => {
      const current = byRequest[item.request];
      if (!current || new Date(item.createdAt) > new Date(current.createdAt)) {
        byRequest[item.request] = item;
      }
    });
    return byRequest;
  }, [followUps]);

  const requests = (procedures || [])
    .filter((request) => request.status === "initiated")
    .sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  const types = [...new Set(requests.map((request) => request.type))];
  const toRemind = requests.filter((r) => !lastFollowUp[r.reference]);

  const term = search.trim().toLowerCase();
  const filtered = requests.filter((request) => {
    const done = Boolean(lastFollowUp[request.reference]);
    const client = clientOf(request);

    return (
      (!type || request.type === type) &&
      (!reminded || (reminded === "done" ? done : !done)) &&
      (!term ||
        [
          request.reference,
          requestTitle(request),
          clientName(request),
          client.mobile,
          client.email,
        ]
          .filter(Boolean)
          .some((value) => value.toLowerCase().includes(term)))
    );
  });

  const stats = [
    { label: "Démarches inachevées", value: requests.length },
    { label: "Clients à relancer", value: toRemind.length },
    { label: "Déjà relancés", value: requests.length - toRemind.length },
  ];

  const resetPaging = (setter) => (event) => {
    setter(event.target.value);
    setVisible(PAGE_SIZE);
  };

  return (
    <>
      <PageHeader
        title="Démarches inachevées"
        lead="Les clients qui ont commencé une démarche sans la payer. Relancez-les pour les aider à la terminer."
      />

      <div className="dash-stats">
        {stats.map((stat) => (
          <div key={stat.label} className="dash-card dash-stat">
            <span>{stat.label}</span>
            <strong>{loading ? "…" : stat.value}</strong>
          </div>
        ))}
      </div>

      <div className="dash-card">
        <div className="dash-filters">
          <input
            type="search"
            placeholder="Rechercher un client, un téléphone, une référence"
            value={search}
            onChange={resetPaging(setSearch)}
            aria-label="Rechercher"
          />
          <select
            value={type}
            onChange={resetPaging(setType)}
            aria-label="Type de démarche"
          >
            <option value="">Tous les types</option>
            {types.map((value) => (
              <option key={value} value={value}>
                {REQUEST_TYPES[value] || value}
              </option>
            ))}
          </select>
          <select
            value={reminded}
            onChange={resetPaging(setReminded)}
            aria-label="Relance"
          >
            <option value="">Toutes</option>
            <option value="todo">À relancer</option>
            <option value="done">Déjà relancées</option>
          </select>
        </div>

        {loading ? (
          <p className="dash-loading">Chargement des démarches…</p>
        ) : filtered.length === 0 ? (
          <EmptyState title="Aucune démarche">
            {requests.length
              ? "Aucune démarche ne correspond à ces critères."
              : "Aucune démarche inachevée pour le moment."}
          </EmptyState>
        ) : (
          <>
            <div className="dash-table-wrap">
              <table className="dash-table is-responsive">
                <thead>
                  <tr>
                    <th>Client</th>
                    <th>Démarche</th>
                    <th>Commencée le</th>
                    <th>Dernière relance</th>
                    <th aria-label="Actions" />
                  </tr>
                </thead>
                <tbody>
                  {filtered.slice(0, visible).map((request) => {
                    const last = lastFollowUp[request.reference];

                    return (
                      <tr key={request.reference}>
                        <td>
                          <span className="dash-cell-title">
                            {clientName(request)}
                          </span>
                          <span className="dash-cell-sub">
                            {clientOf(request).mobile ||
                              clientOf(request).email ||
                              "Pas de contact"}
                          </span>
                        </td>
                        <td data-label="Démarche">
                          {requestTitle(request)}
                          <span className="dash-cell-sub">
                            #{request.reference}
                          </span>
                        </td>
                        <td data-label="Commencée le">
                          {moment(request.createdAt).format("DD/MM/YYYY")}
                        </td>
                        <td data-label="Dernière relance">
                          {last ? (
                            <>
                              {moment(last.createdAt).format("DD/MM/YYYY")}
                              <span className="dash-cell-sub">
                                {FOLLOW_UP_CHANNELS[last.channel] ||
                                  last.channel}
                              </span>
                            </>
                          ) : (
                            <span className="dash-tag">À relancer</span>
                          )}
                        </td>
                        <td>
                          <div className="dash-row-actions">
                            <button
                              type="button"
                              className="dash-link-button"
                              onClick={() =>
                                navigate("/sales/follow-up-details", {
                                  state: { procedure: request },
                                })
                              }
                            >
                              Relancer
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            {filtered.length > visible && (
              <div className="dash-more">
                <button
                  type="button"
                  className="dash-button is-secondary"
                  onClick={() => setVisible((count) => count + PAGE_SIZE)}
                >
                  Afficher plus
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </>
  );
}

export default SalesProcedures;
